import axios from 'axios';
import { updateError } from '../redux/slices/user';
import { dispatch } from '../redux/store';
// config
import { HOST_API, ADMIN_HOST_API } from '../config';
import { setSession } from './jwt';
import { bookingApiRequest, isPublicBookingRequest } from './bookingApiRequest';

// ===== Throttling / Retry Settings =====
const MAX_CONCURRENCY = 10;              // cap concurrent in‑flight requests
const REQUEST_TIMEOUT_MS = 10000;       // axios timeout per request (default; per-call overrideable)
const MAX_RETRIES = 2;                  // idempotent retries (GET/HEAD/OPTIONS) only
const BASE_BACKOFF_MS = 250;            // base backoff for jitter
// Dedupe window — must be >= REQUEST_TIMEOUT_MS so concurrent callers of
// a slow GET share the in-flight promise instead of stampeding the BE.
// Was 8s while timeout was 10s, which let a slow request "expire" mid-flight
// and the next caller would launch a fresh one — pile-up under load.
const MAX_DEDUPE_AGE_MS = 32000;

// Simple promise pool for concurrency limiting
class RequestPool {
    constructor(limit) { this.limit = limit; this.active = 0; this.queue = []; }

    async run(task) {
        if (this.active >= this.limit) {
            await new Promise((resolve) => this.queue.push(resolve));
        }
        this.active += 1;
        try { return await task(); }
        finally { this.active -= 1; this.queue.shift()?.(); }
    }
}
const _pool = new RequestPool(MAX_CONCURRENCY);

// In‑flight dedupe map: key -> { promise, ts }
const _inflight = new Map();

function _isIdempotent(method = 'GET') {
    const m = String(method).toUpperCase();
    return m === 'GET' || m === 'HEAD' || m === 'OPTIONS';
}

function _sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }
function _jitter(base, attempt) { return base * 2 ** attempt + Math.floor(Math.random() * base); }

function _makeDedupeKey(cfg) {
    const method = String(cfg.method || 'GET').toUpperCase();
    const url = cfg.url || '';
    const params = cfg.params ? JSON.stringify(cfg.params) : '';
    // For GET we ignore body to improve cache hit; for POST you could hash a safe subset if desired
    return `${method} ${url} ${params}`;
}

axios.create({
    baseURL: HOST_API,
    timeout: REQUEST_TIMEOUT_MS,
});

axios.interceptors.response.use(
    (response) => response,
    (error) => Promise.reject(error)
);

/**
 * Execute an Axios request with:
 *  - Concurrency limiting (pool)
 *  - In‑flight de‑duplication for idempotent requests
 *  - 429 handling that honors Retry‑After
 *  - Limited retries for transient/network/5xx on idempotent methods
 * Always returns a structured result { ok, status, data, headers, error }
 */
async function execRequest(config) {
    const cfg = { timeout: REQUEST_TIMEOUT_MS, ...config };

    const idempotent = _isIdempotent(cfg.method);
    const key = idempotent ? _makeDedupeKey(cfg) : null;
    const now = Date.now();

    // Purge stale dedupe entries (avoid for..of to satisfy lint rules)
    Array.from(_inflight.entries()).forEach(([k, v]) => {
        if (now - v.ts > MAX_DEDUPE_AGE_MS) _inflight.delete(k);
    });

    if (key && _inflight.has(key)) {
        try {
            const res = await _inflight.get(key).promise;
            return { ok: true, status: res.status, data: res.data, headers: res.headers };
        } catch (err) {
            const resp = err?.response;
            return resp
                ? { ok: false, status: resp.status, data: resp.data, headers: resp.headers, error: err }
                : { ok: false, status: 0, data: null, headers: {}, error: err };
        }
    }

    let attempt = 0;
    const _do = () => _pool.run(() => axios(cfg));

    const attemptRequest = async () => {
        try {
            const res = await _do();
            return res;
        } catch (err) {
            const resp = err?.response;
            const status = resp?.status;

            // 429 Too Many Requests → honor Retry‑After if present
            if (status === 429 && idempotent && attempt < MAX_RETRIES) {
                attempt += 1;
                const ra = resp?.headers?.['retry-after'];
                const wait = ra ? parseInt(String(ra), 10) * 1000 : _jitter(BASE_BACKOFF_MS, attempt);
                const delay = Number.isFinite(wait) ? wait : _jitter(BASE_BACKOFF_MS, attempt);
                await _sleep(delay);
                return attemptRequest();
            }

            // Network errors / timeouts / retriable 5xx (except 501) for idempotent methods
            const retriable = (!resp || (status >= 500 && status !== 501)) || err?.code === 'ECONNABORTED';
            if (retriable && idempotent && attempt < MAX_RETRIES) {
                attempt += 1;
                await _sleep(_jitter(BASE_BACKOFF_MS, attempt));
                return attemptRequest();
            }
            throw err;
        }
    };

    const call = attemptRequest();

    if (key) _inflight.set(key, { promise: call, ts: now });

    try {
        const res = await call;
        return { ok: true, status: res.status, data: res.data, headers: res.headers };
    } catch (err) {
        const resp = err?.response;
        return resp
            ? { ok: false, status: resp.status, data: resp.data, headers: resp.headers, error: err }
            : { ok: false, status: 0, data: null, headers: {}, error: err };
    } finally {
        if (key) _inflight.delete(key);
    }
}

// Token refresh with request coalescing: while a refresh is in‑flight,
// subsequent callers wait for the same result instead of triggering another refresh.
const refreshToken = () => new Promise((resolve, reject) => {
    if (isRefreshing) {
        refreshQueue.push({ resolve, reject });
        return;
    }

    isRefreshing = true;

    axios
        .post(`${HOST_API}/api/v1/auth/token/refresh/`, {
            refresh: localStorage.getItem('refreshToken'),
        })
        .then((response) => {
            if (response.status === 200) {
                const newAccess = response.data.access;
                setSession(newAccess, localStorage.getItem('refreshToken'));
                axios.defaults.headers.common.Authorization = `Bearer ${newAccess}`;
                // Reset BEFORE draining queue: any re-entrant refreshToken() call
                // from a queued caller's retry must see isRefreshing=false, otherwise
                // it pushes to a queue that's already been drained (stranded promise).
                // The old .finally() reset ran AFTER .then(), leaving a micro-window
                // where re-entrants saw isRefreshing=true and got stranded.
                isRefreshing = false;
                refreshQueue.forEach((req) => req.resolve(newAccess));
                refreshQueue = [];
                resolve(newAccess);
            } else {
                throw new Error('Refresh failed');
            }
        })
        .catch((error) => {
            isRefreshing = false; // Reset before rejecting queue (same reason as .then)
            setSession(null, null);
            refreshQueue.forEach((req) => req.reject(error));
            refreshQueue = [];
            if (!window.location.pathname.includes('/login')) {
                window.location.href = '/';
            }
            dispatch(updateError({
                message: 'Session has timed Out! Please Login Again',
                success: "warning"
            }));

            reject(error);
        });
});

let isRefreshing = false;
let refreshQueue = [];

const accessToken = () => localStorage.getItem('accessToken');

/**
 * callApi
 * - Adds Authorization header unless `isUnAuthorized`
 * - Uses execRequest() which applies throttling/dedupe/retries
 * - On 401, performs a single token refresh, then retries once
 * - Optional `options` param can include { timeout: ms } for long-running requests
 */
export async function callApi(uri, method = 'GET', data = null, isUnAuthorized = false, options = {}) {
    // Block requests with undefined/null in ANY path segment — not just practiceId.
    // Frontend code can pass undefined IDs via template strings (e.g. `/leads/${leadId}/`)
    // when state hasn't hydrated yet. These hit the backend as literal "undefined" or "null"
    // strings, causing ValueError: Field 'id' expected a number but got 'undefined'.
    if (uri && (/\/(?:undefined|null)(?:\/|$)/.test(uri))) {
        console.warn('[apiCall] Blocked request with invalid path parameter:', uri);
        return { ok: false, status: 0, data: null, headers: {}, error: 'Invalid request parameter' };
    }
    // Block any request with /undefined/ or /null/ in path segments (e.g. /leads/undefined/treatments/)
    const uriPath = uri?.split('?')[0] || '';
    if (/\/(undefined|null)\//.test(uriPath) || uriPath.endsWith('/undefined') || uriPath.endsWith('/null')) {
        console.warn('[apiCall] Blocked request with undefined/null path segment:', uri);
        return { ok: false, status: 0, data: null, headers: {}, error: 'Invalid path parameter' };
    }
    const isFormData = data instanceof FormData;
    const config = {
        method,
        url: `${HOST_API}${uri}`,
        headers: isFormData ? {} : { 'Content-Type': 'application/json' },
        data,
    };
    if (options.timeout) {
        config.timeout = options.timeout;
    }
    if (!isUnAuthorized) {
        config.headers.Authorization = `Bearer ${accessToken()}`;
    }

    if (isUnAuthorized && isPublicBookingRequest(uri)) {
        return bookingApiRequest(config, options, cfg => _pool.run(() => axios(cfg)));
    }

    // Opt-in cache bust: evict the inflight dedupe entry for this URL before the
    // first execRequest so the GET bypasses the 32s MAX_DEDUPE_AGE_MS window and
    // returns real server state after a mutation.  callApi owns this decision —
    // execRequest is not aware of bustCache at all.
    // Map.delete on a missing key is a safe no-op so no guard needed.
    if (options.bustCache) {
        _inflight.delete(_makeDedupeKey(config));
    }

    // First attempt
    let response = await execRequest(config);

    // 500/503: notify but still return structured response
    if (response.status === 500 || response.status === 503) {
        const endpoint = uri?.split('?')[0] || 'unknown';
        dispatch(updateError({ message: `Something went wrong! (${endpoint})`, success: false, status: response.status }));
    }

    // 401: try token refresh (only when authorized path)
    if (response.status === 401 && !isUnAuthorized) {
        try {
            const newAccessToken = await refreshToken();
            config.headers.Authorization = `Bearer ${newAccessToken}`;
            response = await execRequest(config);
        } catch (err) {
            const resp = err?.response;
            return resp
                ? { ok: false, status: resp.status, data: resp.data, headers: resp.headers, error: err }
                : { ok: false, status: 0, data: null, headers: {}, error: err };
        }
    }

    return response;
}

/**
 * callAdminApi
 * - Always sends Authorization header
 * - Uses execRequest() which applies throttling/dedupe/retries
 * - On 401, performs a token refresh then retries once
 */
export async function callAdminApi(uri, method = 'GET', data = null, options = {}) {
    const isFormData = data instanceof FormData;
    const config = {
        method,
        url: `${ADMIN_HOST_API}${uri}`,
        headers: isFormData ? {} : { 'Content-Type': 'application/json' },
        data,
    };
    if (options && Number.isFinite(options.timeout)) {
        // Per-call timeout override. The default REQUEST_TIMEOUT_MS (10s)
        // is too tight for endpoints that do Celery broadcast RPCs +
        // multiple COUNT(*) queries on a cold cache (e.g. the
        // tasks-migration diagnostics view). Without this knob those
        // calls timed out and returned status=0 even though the BE
        // eventually finished and returned 200.
        config.timeout = options.timeout;
    }
    config.headers.Authorization = `Bearer ${accessToken()}`;

    let response = await execRequest(config);

    if (response.status === 500 || response.status === 503) {
        const endpoint = uri?.split('?')[0] || 'unknown';
        dispatch(updateError({ message: `Something went wrong! (${endpoint})`, success: false, status: response.status }));
    }

    if (response.status === 401) {
        try {
            const newAccessToken = await refreshToken();
            config.headers.Authorization = `Bearer ${newAccessToken}`;
            response = await execRequest(config);
        } catch (err) {
            const resp = err?.response;
            return resp
                ? { ok: false, status: resp.status, data: resp.data, headers: resp.headers, error: err }
                : { ok: false, status: 0, data: null, headers: {}, error: err };
        }
    }

    return response;
}
